// apps/admin/src/pages/system/settings/SettingsPage.tsx
//
// Phase 8.3 Wave 6 Task 41 — System Settings page shell (7-tab strip).
//
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Route mapping (each tab is a `?tab=...` query string for deeplinkability
// + a sibling route in App.tsx for sidebar entries):
//   /system/settings                       → tab=general (default)
//   /system/settings/general               → tab=general
//   /system/settings/merchant-configs      → tab=merchant
//   /system/settings/vendor-configs        → tab=vendor
//   /system/settings/order-configs         → tab=order      (task-42 owns)
//   /system/settings/notifications         → tab=notifications (task-42)
//   /system/settings/security              → tab=security   (task-43)
//   /system/settings/support               → tab=support    (task-43)
//
// This task (41) implements only the 3 tabs General / Merchant / Vendor.
// The remaining 4 tabs render a "Lands in task-42/43" placeholder card so
// the tab strip is complete + reachable. Siblings can swap their placeholders
// for the real tab body without touching this shell.
//
// Permission: every tab requires `system:settings:read` to mount; the
// per-tab `system:settings:write:<namespace>` gate lives inside each tab.

import { useEffect, useMemo, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn, usePermission, PageHeader } from '@printway/ui';

import { useTranslation } from '../../../i18n/useTranslation.js';
import { ensureP83Loaded } from '../../../i18n/index.js';
import { GeneralTab } from '../../settings/GeneralTab.js';
import { MerchantConfigsTab } from '../../settings/MerchantConfigsTab.js';
import { VendorConfigsTab } from '../../settings/VendorConfigsTab.js';

// ---------------------------------------------------------------------------
// Tab catalog — id matches the BE namespace string 1:1. `path` is the
// route alias that the sidebar uses; the URL is the source of truth so
// users can paste-share a deeplink to a single tab.
// ---------------------------------------------------------------------------

interface TabDef {
  id:
    | 'general'
    | 'merchant'
    | 'vendor'
    | 'order'
    | 'notifications'
    | 'security'
    | 'support';
  label: string;
  path: string;
  /** When true, this tab is owned by a sibling task (42/43). */
  placeholder?: boolean;
}

const TABS: ReadonlyArray<TabDef> = [
  { id: 'general', label: 'General', path: '/system/settings/general' },
  {
    id: 'merchant',
    label: 'Merchant Configs',
    path: '/system/settings/merchant-configs',
  },
  {
    id: 'vendor',
    label: 'Vendor Configs',
    path: '/system/settings/vendor-configs',
  },
  {
    id: 'order',
    label: 'Order Configs',
    path: '/system/settings/order-configs',
    placeholder: true,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    path: '/system/settings/notifications',
    placeholder: true,
  },
  {
    id: 'security',
    label: 'Security',
    path: '/system/settings/security',
    placeholder: true,
  },
  {
    id: 'support',
    label: 'Support',
    path: '/system/settings/support',
    placeholder: true,
  },
];

const TAB_LABEL_KEYS: Record<TabDef['id'], string> = {
  general: 'system.settings.tabs.general',
  merchant: 'system.settings.tabs.merchantConfigs',
  vendor: 'system.settings.tabs.vendorConfigs',
  order: 'system.settings.tabs.orderConfigs',
  notifications: 'system.settings.tabs.notifications',
  security: 'system.settings.tabs.security',
  support: 'system.settings.tabs.support',
};

function pickActiveTab(pathname: string): TabDef['id'] {
  // Exact-path match first (so `/system/settings/general` doesn't fall through
  // to the prefix-match branch and pick `/system/settings` accidentally).
  for (const tab of TABS) {
    if (pathname === tab.path) return tab.id;
  }
  // Bare `/system/settings` defaults to General.
  return 'general';
}

function PlaceholderTab({ tabLabel }: { tabLabel: string }): JSX.Element {
  const { t } = useTranslation('p83');
  return (
    <div className="rounded-lg border border-dashed border-pw-border bg-pw-surface p-8 text-center text-sm text-pw-text-muted">
      <p className="font-medium text-pw-text">
        {t('system.settings.placeholder.title', { tab: tabLabel })}
      </p>
      <p className="mt-1 text-xs">
        {t('system.settings.placeholder.body')}
      </p>
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { t, locale } = useTranslation('p83');
  const location = useLocation();
  const navigate = useNavigate();
  const canRead = usePermission('system:settings:read');

  useEffect(() => {
    void ensureP83Loaded(locale);
  }, [locale]);

  const activeId = useMemo(
    () => pickActiveTab(location.pathname),
    [location.pathname],
  );

  if (!canRead) {
    return (
      <main className="p-6">
        <div className="rounded-lg border border-pw-border bg-pw-surface p-6 text-sm text-pw-text-muted">
          {t('system.settings.deniedPrefix')}{' '}
          <code className="font-mono">system:settings:read</code>.
        </div>
      </main>
    );
  }

  // TABS is a non-empty readonly tuple — `TABS.find` may return undefined per
  // strict-null but `pickActiveTab` always falls back to 'general' which is
  // the first entry, so the `find` will resolve. Default-fallback handles the
  // theoretical empty case for TypeScript's benefit.
  const activeTab =
    TABS.find((t) => t.id === activeId) ??
    ({
      id: 'general',
      label: 'General',
      path: '/system/settings/general',
    } satisfies TabDef);

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="system-settings-page">
      <PageHeader title={t('system.settings.title')} subtitle={t('system.settings.subtitle')} />

      <nav
        role="tablist"
        aria-label={t('system.settings.tablistAria')}
        className="flex flex-wrap gap-1 border-b border-pw-border"
        data-testid="system-settings-tablist"
      >
        {TABS.map((tab) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`system-settings-panel-${tab.id}`}
              id={`system-settings-tab-${tab.id}`}
              onClick={() => navigate(tab.path)}
              className={cn(
                '-mb-px inline-flex h-10 items-center gap-2 border-b-2 px-4 text-sm font-medium transition-colors',
                active
                  ? 'border-pw-primary text-pw-primary-strong'
                  : 'border-transparent text-pw-text-muted hover:border-pw-border hover:text-pw-text',
              )}
              data-testid={`system-settings-tab-${tab.id}`}
            >
              {t(TAB_LABEL_KEYS[tab.id])}
              {tab.placeholder ? (
                <span className="rounded-full border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-pw-text-muted">
                  {t('system.settings.soonBadge')}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <section
        role="tabpanel"
        id={`system-settings-panel-${activeTab.id}`}
        aria-labelledby={`system-settings-tab-${activeTab.id}`}
      >
        {activeTab.id === 'general' ? <GeneralTab /> : null}
        {activeTab.id === 'merchant' ? <MerchantConfigsTab /> : null}
        {activeTab.id === 'vendor' ? <VendorConfigsTab /> : null}
        {activeTab.placeholder ? (
          <PlaceholderTab tabLabel={t(TAB_LABEL_KEYS[activeTab.id])} />
        ) : null}
      </section>
    </main>
  );
}

export default SettingsPage;
